import { useMutation } from '@apollo/client';
import { useI18n } from '@i18n';
import { Box, Button } from '@mui/material';
import { consumption_source_type_enum, DataCollectionConsumptionFragment } from '@predium/client-graphql';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import { SnackbarTimeouts } from '../../components/NotistackProvider';
import AccessRightsWrapper from '../../components/permission-tooltips/AccessRightsWrapper';
import PreDialog, { PreDialogBody } from '../../components/presentations/PreDialog/PreDialog';
import Scrollbar from '../../components/Scrollbar';
import { WarningModal } from '../../components/WarningModal';
import { DELETE_CONSUMPTION } from '../../graphql/DataCollection.mutations';
import { GET_CONSUMPTION_SUMMARIES, GET_CONSUMPTIONS } from '../../graphql/DataCollection.queries';
import AreaConsumptionInvoiceDraft from '../../sections/DataCollection/Building/Consumption/AreaConsumptionInvoiceDraft';
import AreaSingleConsumptionForm from '../../sections/DataCollection/Building/Drafts/AreaSingleConsumptionForm';

type Props = {
  buildingId: number;
  hasEditAccess: boolean;
  isOpen: boolean;
  onClose: () => void;
  consumptionData: DataCollectionConsumptionFragment | null;
};

export default function DataCollectionSingleConsumptionModal({
  buildingId,
  hasEditAccess,
  isOpen,
  onClose,
  consumptionData,
}: Props) {
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isChildFormDirty, setIsChildFormDirty] = useState(false);
  const [isChildFormInvalid, setIsChildFormInvalid] = useState(false);
  const [isOpenWarningDialog, setIsOpenWarningDialog] = useState(false);

  const [deleteConsumptionMutation] = useMutation(DELETE_CONSUMPTION, {
    variables: {
      id: consumptionData?.id!,
    },
    onCompleted: () => {
      enqueueSnackbar(t('DataCollection_ConsumptionDelete-success'), {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      });
      onClose();
    },
    onError: () => {
      enqueueSnackbar(t('DataCollection_ConsumptionDelete-error'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    },
    update: (cache, { data }) => {
      if (data?.delete_consumption_by_pk) {
        const normalizedId = cache.identify({
          id: data.delete_consumption_by_pk.id,
          __typename: data.delete_consumption_by_pk.__typename,
        });
        cache.evict({ id: normalizedId });
        cache.gc();
      }
    },
    refetchQueries: [GET_CONSUMPTION_SUMMARIES, GET_CONSUMPTIONS],
  });

  return (
    <>
      <PreDialog
        open={isOpen}
        onClose={() => {
          if (isChildFormDirty) {
            setIsOpenWarningDialog(true);
          } else {
            onClose();
          }
        }}
        type="definedByChildren"
        maxWidth="sm"
        fullWidth
      >
        <AreaSingleConsumptionForm
          buildingId={buildingId}
          consumptionData={consumptionData!}
          handleClose={onClose}
          setIsChildFormDirty={setIsChildFormDirty}
          setIsChildFormInvalid={setIsChildFormInvalid}
          renderChildren={(submitForm) => (
            <>
              <PreDialogBody
                dialogtitle={
                  consumptionData?.source_type_id === consumption_source_type_enum.ESTIMATION
                    ? t('DataCollection_ConsumptionModalTitle-estimation')
                    : t('PageName_DataCollection_Consumption')
                }
                content={
                  <Scrollbar
                    sx={{
                      // make actions sticky and content scrollable by fixing maxHeight
                      // full height - dialog top margin - dialog bottom margin - header - actions
                      maxHeight: 'calc(100vh - 32px - 32px - 76px - 84px)',
                    }}
                  >
                    {isChildFormInvalid ? 'Invalid' : 'Valid'}
                    <AreaConsumptionInvoiceDraft isSingleConsumption disabled={!hasEditAccess} />
                  </Scrollbar>
                }
                actions={
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <AccessRightsWrapper hasAccess={hasEditAccess}>
                      <Button
                        variant="text"
                        color="error"
                        disabled={!consumptionData}
                        onClick={() => setDeleteModalOpen(true)}
                      >
                        {t('General_Delete')}
                      </Button>
                    </AccessRightsWrapper>

                    <AccessRightsWrapper hasAccess={hasEditAccess}>
                      <Button variant="contained" type="submit">
                        {t('General_Save')}
                      </Button>
                    </AccessRightsWrapper>
                  </Box>
                }
              />

              {isOpenWarningDialog ? (
                <WarningModal
                  title={t('General_UnsavedChanges')}
                  open={isOpenWarningDialog}
                  description={t('DataCollection_UnsavedChangesInDraft-Message')}
                  onAcknowledge={() => {
                    isChildFormInvalid
                      ? setIsOpenWarningDialog(false)
                      : submitForm().then(() => {
                          setIsOpenWarningDialog(false);
                          onClose();
                        });
                  }}
                  onClose={() => {
                    setIsOpenWarningDialog(false);
                    onClose();
                  }}
                  buttonText={isChildFormInvalid ? t('General_BackToEditing') : t('General_Save')}
                  cancelText={t('General_Leave')}
                />
              ) : null}
            </>
          )}
        />
      </PreDialog>

      {/* Dont render all dialogs in a list but dont cut animation short. */}
      {isOpen ? (
        <DeleteConfirmationModal
          open={deleteModalOpen}
          title={t('DataCollection_DeleteModalTitle-Consumption')}
          description={t('DataCollection_DeleteModalDescription-Consumption')}
          onClose={() => {
            setDeleteModalOpen(false);
          }}
          onDelete={async () => {
            deleteConsumptionMutation();
          }}
        />
      ) : null}
    </>
  );
}
